/**
 * Onboarding tour definitions — structure from manifest + default copy merge.
 */

import {
  DEFAULT_TOUR_COPY,
  TOUR_STRUCTURE_MANIFEST,
} from '../../shared/onboarding/tourStructure.manifest';
import { mergeTourDefinitions } from '../../shared/onboarding/mergeTourDefinitions';
import {
  DASHBOARD_ROLE_TOUR_DEFINITIONS,
  formatDashboardRoleTourContent,
  roleTabTourTarget,
  shouldSkipRoleTabTourStep,
} from '../../shared/onboarding/dashboardRoleTourCopy';
import {
  buildAttendeeDashboardTourSteps,
  getAttendeeDashboardStepSpec,
  shouldSkipAttendeeDashboardStep,
  type AttendeeDashboardTabSlug,
} from '../../shared/onboarding/attendeeDashboardTour';
import {
  buildModuleDashboardTourSteps,
  COORDINATOR_DASHBOARD_STEP_SPECS,
  getModuleDashboardStepSpec,
  INSTRUCTOR_DASHBOARD_STEP_SPECS,
  shouldSkipModuleDashboardStep,
  SUPERVISOR_DASHBOARD_STEP_SPECS,
  TRAINING_MANAGER_DASHBOARD_STEP_SPECS,
  type ModuleDashboardStepSpec,
} from '../../shared/onboarding/dashboardModuleTour';
import type { BeforeActionAllowlist } from '../../shared/onboarding/types';
import { shouldSkipRoleSwitcherTourStep } from '../../shared/onboarding/roleSwitcherTourStep';
import {
  buildNavReferenceTourSteps,
  getNavReferenceStepSpec,
  shouldSkipNavReferenceStep,
} from '../../shared/onboarding/navReferenceTour';
import {
  TOUR_DOM_EVENTS,
  type NavTourDropdownMenu,
} from '../components/onboarding/onboardingDomEvents';
import { waitForTarget, queryTourTarget, isTourTargetVisible } from '../components/onboarding/engine/waitForTarget';
import type { CoordinatorLabTourSlug } from '../../shared/onboarding/coordinatorLearningLabTours';
import { isDashboardPath } from '../../shared/onboarding/missionRouteContract';
import { coerceTourPlainText } from '../../shared/utils/coerceEventListingText';
import { isLabMissionTour } from '../../shared/onboarding/tourCatalog';
import { getLearningLabPlaybook } from '../../shared/onboarding/learningLabPlaybooks';
import { buildMissionTourSteps } from '../../shared/onboarding/buildMissionTourSteps';
import { enrichMissionTourStep } from './enrichMissionTourStep';
import type { OnboardingFlow, TourStep } from '../types/onboarding';

type StepWithBeforeAction = {
  stepName: string;
  target?: string | HTMLElement;
  beforeAction?: BeforeActionAllowlist | string;
};

const NAV_DROPDOWN_OPEN_DELAY_MS = 300;

function isNavDropdownMenuOpen(menu: NavTourDropdownMenu): boolean {
  if (menu === 'user') {
    const btn = document.getElementById('user-menu-button');
    return btn?.getAttribute('aria-expanded') === 'true';
  }

  const btn = document.getElementById(`${menu}-menu-button`);
  return btn?.getAttribute('aria-expanded') === 'true';
}

async function ensureNavDropdownOpen(
  menu: NavTourDropdownMenu,
  step: StepWithBeforeAction,
): Promise<void> {
  if (typeof step.target === 'string') {
    const existing = queryTourTarget(step.target);
    if (existing && isTourTargetVisible(existing)) {
      return;
    }
  }

  if (isNavDropdownMenuOpen(menu)) {
    await waitForStepTarget(step);
    return;
  }

  dispatchOpenNavDropdown(menu);
  await new Promise((resolve) =>
    window.setTimeout(resolve, NAV_DROPDOWN_OPEN_DELAY_MS),
  );
  await waitForStepTarget(step);
  window.dispatchEvent(new Event('resize'));
}

async function scrollTourTargetIntoView(step: StepWithBeforeAction): Promise<void> {
  const selector = typeof step.target === 'string' ? step.target : '';
  const match = selector.match(/data-tour(?:-stable)?=["']([^"']+)["']/);
  const id = match?.[1];
  const el = id
    ? document.querySelector(`[data-tour-stable="${id}"], [data-tour="${id}"]`)
    : document.querySelector(selector);
  el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
}

async function waitForStepTarget(step: StepWithBeforeAction): Promise<void> {
  if (typeof step.target !== 'string') return;
  await waitForTarget(step.target, { timeoutMs: 3000 });
}

function dispatchOpenNavDropdown(menu: NavTourDropdownMenu): void {
  window.dispatchEvent(
    new CustomEvent(TOUR_DOM_EVENTS.OPEN_NAV_DROPDOWN, { detail: { menu } }),
  );
}

export function resolveBeforeAction(
  step: StepWithBeforeAction,
): (() => void | Promise<void>) | undefined {
  if (step.beforeAction === 'scrollIntoView') {
    return async () => {
      await scrollTourTargetIntoView(step);
    };
  }

  if (step.beforeAction === 'openHelpSpeedDial') {
    return async () => {
      window.dispatchEvent(new CustomEvent(TOUR_DOM_EVENTS.OPEN_HELP_SPEED_DIAL));
      await new Promise((resolve) => window.setTimeout(resolve, 400));
      window.dispatchEvent(new Event('resize'));
    };
  }

  if (step.beforeAction === 'closeNavDropdowns') {
    return async () => {
      window.dispatchEvent(new CustomEvent(TOUR_DOM_EVENTS.CLOSE_NAV_DROPDOWNS));
      await new Promise((resolve) => window.setTimeout(resolve, 50));
    };
  }

  if (step.beforeAction === 'closeNavDropdownsAndScroll') {
    return async () => {
      window.dispatchEvent(new CustomEvent(TOUR_DOM_EVENTS.CLOSE_NAV_DROPDOWNS));
      await new Promise((resolve) => window.setTimeout(resolve, 50));
      await scrollTourTargetIntoView(step);
    };
  }

  if (step.beforeAction === 'openNavBrowseDropdown') {
    return async () => {
      await ensureNavDropdownOpen('browse', step);
    };
  }

  if (step.beforeAction === 'openNavAboutDropdown') {
    return async () => {
      await ensureNavDropdownOpen('aboutus', step);
    };
  }

  if (step.beforeAction === 'openNavProductsDropdown') {
    return async () => {
      await ensureNavDropdownOpen('products', step);
    };
  }

  if (step.beforeAction === 'openNavUserMenu') {
    return async () => {
      await ensureNavDropdownOpen('user', step);
    };
  }

  if (step.beforeAction === 'enterLearningLab') {
    return async () => {
      await new Promise<void>((resolve) => {
        window.dispatchEvent(
          new CustomEvent(TOUR_DOM_EVENTS.ENTER_LEARNING_LAB, {
            detail: { resolve },
          }),
        );
      });
      await new Promise((resolve) => window.setTimeout(resolve, 200));
    };
  }

  if (step.beforeAction === 'switchCoordinatorTab') {
    return async () => {
      const slug =
        (step as TourStep & { metadata?: { coordinatorTabSlug?: string } })
          .metadata?.coordinatorTabSlug ?? 'members';
      const params = new URLSearchParams();
      params.set('role', 'coordinator');
      params.set('tab', slug);
      params.set('learningLab', '1');
      const dashboardPath = `/dashboard?${params.toString()}`;

      if (!isDashboardPath(window.location.pathname)) {
        window.dispatchEvent(
          new CustomEvent(TOUR_DOM_EVENTS.NAVIGATE, {
            detail: { path: dashboardPath, replace: true },
          }),
        );
        await new Promise((resolve) => window.setTimeout(resolve, 450));
      } else {
        window.dispatchEvent(
          new CustomEvent(TOUR_DOM_EVENTS.SWITCH_DASHBOARD_ROLE, {
            detail: { role: 'coordinator' },
          }),
        );
        window.dispatchEvent(
          new CustomEvent(TOUR_DOM_EVENTS.SWITCH_DASHBOARD_MODULE_TAB, {
            detail: { module: 'coordinator', coordinatorTabSlug: slug },
          }),
        );
        const current = new URLSearchParams(window.location.search);
        current.set('role', 'coordinator');
        current.set('tab', slug);
        current.set('learningLab', '1');
        window.history.replaceState(
          {},
          '',
          `${window.location.pathname}?${current.toString()}`,
        );
        await new Promise((resolve) => window.setTimeout(resolve, 350));
      }
      await scrollTourTargetIntoView(step);
    };
  }

  if (step.beforeAction === 'navigateLearningLabRoute') {
    return async () => {
      const path =
        (step as TourStep & { metadata?: { path?: string } }).metadata?.path ??
        '/coordinator-event-create';
      window.dispatchEvent(
        new CustomEvent(TOUR_DOM_EVENTS.NAVIGATE, {
          detail: { path: `${path}?learningLab=1` },
        }),
      );
    };
  }

  if (step.beforeAction === 'openEventCancelModal') {
    return async () => {
      window.dispatchEvent(new CustomEvent(TOUR_DOM_EVENTS.OPEN_EVENT_CANCEL_MODAL));
      await new Promise((resolve) => window.setTimeout(resolve, 300));
    };
  }

  if (step.beforeAction === 'openCreateUserModal') {
    return async () => {
      window.dispatchEvent(new CustomEvent(TOUR_DOM_EVENTS.OPEN_CREATE_USER_MODAL));
      await new Promise((resolve) => window.setTimeout(resolve, 300));
    };
  }

  if (step.beforeAction === 'setEmbedTab') {
    return async () => {
      const tab =
        (step as TourStep & { metadata?: { embedTab?: string } }).metadata
          ?.embedTab ?? 'settings';
      const params = new URLSearchParams(window.location.search);
      params.set('embedTab', tab);
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}?${params.toString()}`,
      );
      window.dispatchEvent(
        new CustomEvent(TOUR_DOM_EVENTS.SET_EMBED_TAB, { detail: { tab } }),
      );
      await new Promise((resolve) => window.setTimeout(resolve, 250));
    };
  }

  if (step.beforeAction === 'focusTarget') {
    return async () => {
      await scrollTourTargetIntoView(step);
      const el =
        typeof step.target === 'string'
          ? queryTourTarget(step.target)
          : step.target;
      if (el instanceof HTMLElement) {
        el.focus({ preventScroll: true });
      }
    };
  }

  return undefined;
}

export function enrichClientTourStep(step: TourStep): TourStep {
  return {
    ...step,
    beforeAction: resolveBeforeAction(step),
  };
}

/** One manifest step becomes intro + one spotlight step per visible role tab (coordinator tour only). */
function expandRoleSwitcherStep(step: TourStep, tourId: string): TourStep[] {
  if (step.stepName !== 'dash_role_switcher') {
    return [enrichClientTourStep(step)];
  }

  if (tourId !== 'dashboard_coordinator') {
    return [];
  }

  const introStep: TourStep = enrichClientTourStep({
    ...step,
    stepName: 'dash_role_switcher_intro',
    target: '[data-tour-stable="role-switcher"]',
    placement: 'bottom',
    title: step.title ?? 'Your role dashboards',
    content:
      step.content ??
      'Each tab opens a different dashboard for a role assigned to your account. The next steps walk through each tab and what you can do there.',
    skipIf: shouldSkipRoleSwitcherTourStep,
    beforeAction: 'scrollIntoView' as BeforeActionAllowlist,
  });

  const roleSteps = DASHBOARD_ROLE_TOUR_DEFINITIONS.map((role) =>
    enrichClientTourStep({
      stepName: `dash_role_tab_${role.key}`,
      target: roleTabTourTarget(role.key),
      placement: 'bottom',
      title: role.label,
      content: formatDashboardRoleTourContent(role),
      skipIf: () => shouldSkipRoleTabTourStep(role.key),
      beforeAction: 'scrollIntoView' as BeforeActionAllowlist,
    }),
  );

  return [introStep, ...roleSteps];
}

function enrichAttendeeDashboardTourStep(step: TourStep): TourStep {
  const spec = getAttendeeDashboardStepSpec(step.stepName);
  const tabSlug = (spec?.attendeeTabSlug ??
    step.metadata?.attendeeTabSlug) as AttendeeDashboardTabSlug | undefined;

  const beforeAction = tabSlug
    ? async () => {
        window.dispatchEvent(
          new CustomEvent(TOUR_DOM_EVENTS.SWITCH_ATTENDEE_TAB, {
            detail: { tabSlug },
          }),
        );
        await new Promise((resolve) => window.setTimeout(resolve, 350));
        await scrollTourTargetIntoView(step);
      }
    : resolveBeforeAction({
        ...step,
        beforeAction: 'scrollIntoView' as BeforeActionAllowlist,
      });

  return {
    ...step,
    skipIf: spec ? () => shouldSkipAttendeeDashboardStep(spec) : step.skipIf,
    beforeAction,
  };
}

function expandAttendeeDashboardTour(tour: OnboardingFlow): OnboardingFlow {
  const copyByStep = Object.fromEntries(
    tour.steps.map((step) => [
      step.stepName ?? '',
      { title: step.title?.toString(), content: step.content?.toString() },
    ]),
  );

  const mergedCopy = {
    ...DEFAULT_TOUR_COPY.dashboard_attendee,
    ...copyByStep,
  };

  const welcomeStep = tour.steps.find((step) => step.stepName === 'dash_welcome');
  const welcome = welcomeStep
    ? enrichClientTourStep({
        ...welcomeStep,
        beforeAction: 'scrollIntoView' as BeforeActionAllowlist,
      })
    : null;

  const tabSteps = buildAttendeeDashboardTourSteps(mergedCopy).map(
    enrichAttendeeDashboardTourStep,
  );

  return {
    ...tour,
    estimatedDurationMinutes: 6,
    steps: welcome ? [welcome, ...tabSteps] : tabSteps,
  };
}

function embedSubTabFromAnchor(anchor: string | undefined): string | null {
  if (!anchor?.startsWith('embed-tab-')) {
    return null;
  }
  return anchor.slice('embed-tab-'.length);
}

async function switchEmbedSubTab(tab: string): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  params.set('embedTab', tab);
  window.history.replaceState(
    {},
    '',
    `${window.location.pathname}?${params.toString()}`,
  );
  window.dispatchEvent(
    new CustomEvent(TOUR_DOM_EVENTS.SET_EMBED_TAB, { detail: { tab } }),
  );
  await new Promise((resolve) => window.setTimeout(resolve, 250));
}

function enrichModuleDashboardTourStep(
  step: TourStep,
  specs: readonly ModuleDashboardStepSpec[],
): TourStep {
  const spec = getModuleDashboardStepSpec(specs, step.stepName);
  const embedSubTab = embedSubTabFromAnchor(spec?.tourAnchor);
  const needsTabSwitch =
    spec?.coordinatorTabSlug !== undefined ||
    spec?.trainingManagerTabIndex !== undefined ||
    spec?.supervisorTabIndex !== undefined;

  const beforeAction =
    needsTabSwitch || embedSubTab
      ? async () => {
          if (needsTabSwitch) {
            window.dispatchEvent(
              new CustomEvent(TOUR_DOM_EVENTS.SWITCH_DASHBOARD_MODULE_TAB, {
                detail: {
                  module: spec?.module,
                  coordinatorTabSlug: spec?.coordinatorTabSlug,
                  trainingManagerTabIndex: spec?.trainingManagerTabIndex,
                  supervisorTabIndex: spec?.supervisorTabIndex,
                },
              }),
            );
            await new Promise((resolve) => window.setTimeout(resolve, 350));
          }
          if (embedSubTab) {
            await switchEmbedSubTab(embedSubTab);
          }
          await scrollTourTargetIntoView(step);
        }
      : resolveBeforeAction({
          ...step,
          beforeAction: 'scrollIntoView' as BeforeActionAllowlist,
        });

  return {
    ...step,
    skipIf: spec ? () => shouldSkipModuleDashboardStep(spec) : step.skipIf,
    beforeAction,
  };
}

function expandModuleDashboardTour(
  tour: OnboardingFlow,
  copyKey: keyof typeof DEFAULT_TOUR_COPY,
  specs: readonly ModuleDashboardStepSpec[],
  options?: { expandRoleSwitcher?: boolean; estimatedDurationMinutes?: number },
): OnboardingFlow {
  const copyByStep = Object.fromEntries(
    tour.steps.map((step) => [
      step.stepName ?? '',
      { title: step.title?.toString(), content: step.content?.toString() },
    ]),
  );

  const mergedCopy = {
    ...DEFAULT_TOUR_COPY[copyKey],
    ...copyByStep,
  };

  const specStepNames = new Set(specs.map((spec) => spec.stepName));
  const steps: TourStep[] = [];

  for (const manifestStep of tour.steps) {
    if (manifestStep.stepName === 'dash_welcome') {
      steps.push(
        enrichClientTourStep({
          ...manifestStep,
          beforeAction: 'scrollIntoView' as BeforeActionAllowlist,
        }),
      );
      continue;
    }

    if (
      manifestStep.stepName === 'dash_role_switcher' &&
      options?.expandRoleSwitcher
    ) {
      steps.push(...expandRoleSwitcherStep(manifestStep, tour.id));
      continue;
    }

    if (manifestStep.stepName && specStepNames.has(manifestStep.stepName)) {
      continue;
    }

    if (manifestStep.stepName?.startsWith('dashboard_role_tab_')) {
      continue;
    }

    steps.push(enrichClientTourStep(manifestStep));
  }

  const moduleSteps = buildModuleDashboardTourSteps(specs, mergedCopy).map((step) =>
    enrichModuleDashboardTourStep(step as TourStep, specs),
  );

  steps.push(...moduleSteps);

  return {
    ...tour,
    estimatedDurationMinutes:
      options?.estimatedDurationMinutes ?? tour.estimatedDurationMinutes,
    steps,
  };
}

function expandRedirectRoleDashboardTour(
  tour: OnboardingFlow,
  roleKey: string,
): OnboardingFlow {
  return {
    ...tour,
    steps: tour.steps.map((step) => {
      if (step.stepName === 'dash_welcome') {
        return enrichClientTourStep({
          ...step,
          beforeAction: 'scrollIntoView' as BeforeActionAllowlist,
        });
      }

      if (step.stepName?.startsWith('dashboard_role_tab_')) {
        return enrichClientTourStep({
          ...step,
          target: roleTabTourTarget(roleKey),
          skipIf: () => shouldSkipRoleTabTourStep(roleKey),
          beforeAction: 'scrollIntoView' as BeforeActionAllowlist,
        });
      }

      return enrichClientTourStep(step);
    }),
  };
}

function expandNavReferenceTour(tour: OnboardingFlow): OnboardingFlow {
  const copyByStep = Object.fromEntries(
    tour.steps.map((step) => [
      step.stepName ?? '',
      { title: step.title?.toString(), content: step.content?.toString() },
    ]),
  );

  const mergedCopy = {
    ...DEFAULT_TOUR_COPY.nav_reference,
    ...copyByStep,
  };

  const steps = buildNavReferenceTourSteps(mergedCopy).map((step) =>
    enrichClientTourStep({
      ...step,
      skipIf: () => {
        const spec = getNavReferenceStepSpec(step.stepName);
        return spec ? shouldSkipNavReferenceStep(spec) : false;
      },
    }),
  );

  return {
    ...tour,
    steps,
  };
}

function expandCoordinatorLearningLabTour(tour: OnboardingFlow): OnboardingFlow {
  if (!isLabMissionTour(tour.id)) {
    return tour;
  }

  const playbook = getLearningLabPlaybook(tour.id);
  const steps: TourStep[] = [];

  for (const manifestStep of tour.steps) {
    if (manifestStep.stepName === 'lab_enter') {
      steps.push(
        enrichClientTourStep({
          ...manifestStep,
          title: 'Enter the Learning Lab',
          content:
            'Practice with sample agency data. Your real agency is never changed.',
          skipIf: () =>
            document.querySelector('[data-tour-stable="learning-lab-banner"]') !==
            null,
        }),
      );
      continue;
    }
    if (manifestStep.stepName === 'lab_expand') {
      continue;
    }
    steps.push(enrichClientTourStep(manifestStep));
  }

  const missionSteps = (playbook ? buildMissionTourSteps(playbook) : []).map(
    (step) =>
      enrichMissionTourStep(
        {
          stepName: step.stepName,
          target: step.target,
          placement: step.placement as TourStep['placement'],
          title: step.title,
          content: step.content,
          tip: step.tip,
          metadata: step.metadata,
        },
        tour.id as CoordinatorLabTourSlug,
      ),
  );

  return {
    ...tour,
    steps: [...steps, ...missionSteps],
  };
}

/** Coerce CMS/API tour copy so step titles cannot trigger React error #130. */
function sanitizeTourFlowForRender(tour: OnboardingFlow): OnboardingFlow {
  return {
    ...tour,
    name: coerceTourPlainText(tour.name, tour.id),
    description: coerceTourPlainText(tour.description, ''),
    steps: tour.steps.map((step) => ({
      ...step,
      title: coerceTourPlainText(step.title, step.stepName ?? 'Step'),
      content: coerceTourPlainText(step.content, ''),
      tip: step.tip != null ? coerceTourPlainText(step.tip, '') : step.tip,
    })),
  };
}

/** Runtime step guards + beforeAction handlers (not serializable in API catalog). */
export function enrichClientTourFlows(tours: OnboardingFlow[]): OnboardingFlow[] {
  return tours.map((tour) => {
    const enriched = (() => {
    if (isLabMissionTour(tour.id)) {
      return expandCoordinatorLearningLabTour(tour);
    }
    switch (tour.id) {
      case 'nav_reference':
        return expandNavReferenceTour(tour);
      case 'dashboard_attendee':
        return expandAttendeeDashboardTour(tour);
      case 'dashboard_coordinator':
        return expandModuleDashboardTour(
          tour,
          'dashboard_coordinator',
          COORDINATOR_DASHBOARD_STEP_SPECS,
          { estimatedDurationMinutes: 3 },
        );
      case 'dashboard_training_manager':
        return expandModuleDashboardTour(
          tour,
          'dashboard_training_manager',
          TRAINING_MANAGER_DASHBOARD_STEP_SPECS,
          { estimatedDurationMinutes: 5 },
        );
      case 'dashboard_supervisor':
        return expandModuleDashboardTour(
          tour,
          'dashboard_supervisor',
          SUPERVISOR_DASHBOARD_STEP_SPECS,
          { estimatedDurationMinutes: 4 },
        );
      case 'dashboard_instructor':
        return expandModuleDashboardTour(
          tour,
          'dashboard_instructor',
          INSTRUCTOR_DASHBOARD_STEP_SPECS,
          { estimatedDurationMinutes: 3 },
        );
      case 'dashboard_tacticore':
        return expandRedirectRoleDashboardTour(tour, 'tacticore');
      case 'dashboard_k9_ops':
        return expandRedirectRoleDashboardTour(tour, 'k9_ops');
      default:
        return {
          ...tour,
          steps: tour.steps.map((step) => enrichClientTourStep(step)),
        };
    }
    })();
    return sanitizeTourFlowForRender(enriched);
  });
}

export const onboardingTours: OnboardingFlow[] = enrichClientTourFlows(
  mergeTourDefinitions(
    TOUR_STRUCTURE_MANIFEST.tours,
    DEFAULT_TOUR_COPY,
  ) as OnboardingFlow[],
);

export function getTourById(id: string): OnboardingFlow | undefined {
  return onboardingTours.find((tour) => tour.id === id);
}

export function getToursForRole(role: string): OnboardingFlow[] {
  return onboardingTours.filter((tour) => {
    if (!tour.active) return false;
    if (!tour.requiredRole) return true;
    const roles = Array.isArray(tour.requiredRole)
      ? tour.requiredRole
      : [tour.requiredRole];
    return roles.includes(role);
  });
}

export default onboardingTours;
